import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

// <ScrollRestoration> lands on the URL's fragment in a layout effect, which on a
// cold load is the one moment it cannot be right. The web fonts have not swapped
// in yet, so the document is taller than it is about to be — measured here, the
// FAQ sat 664px lower than its settled position — and the offset it computes
// belongs to a layout that collapses a frame later. On top of that,
// `html { scroll-behavior: smooth }` makes the move an animation, so it is still
// travelling when the page shrinks beneath it, overshoots what is by then past
// the end of the document, and clamps at the bottom.
//
// Landing once more after the fonts resolve fixes the service pages, but not the
// landing page, whose lazily loaded cards arrive later still and push the
// contact section down another 84px. There is no single moment when a page has
// finished moving, so rather than guess at one this watches the target and lands
// again whenever it shifts, until the page holds still or the reader takes over.
//
// Instantly, every time: `behavior: "auto"` defers to the CSS property rather
// than overriding it, and "instant" is the only value that beats the stylesheet.
const SETTLE_BUDGET_MS = 2500;

// What the reader does to take the page over. Deliberately not `scroll`: the
// document shrinking under a landing clamps the scroll position and fires one of
// those, which is the page moving, not the reader — reading it as intent was
// what made an earlier version give up at the very moment it needed to correct.
const READER_TOOK_OVER = ["wheel", "touchstart", "keydown"];

export default function useFragmentLanding() {
  const location = useLocation();

  // The router's idea of where we are, readable from inside the watcher without
  // restarting it. `window.location` will not do: the layout route this runs in
  // outlives every navigation, and under a memory router the browser's URL never
  // moves at all, so a watcher checking it would never notice it had been left
  // behind.
  const pathname = useRef(location.pathname);
  pathname.current = location.pathname;

  useEffect(() => {
    const id = location.hash.slice(1);
    if (!id) return undefined;

    let frame = null;
    let lastTargetY = null;
    const deadline = Date.now() + SETTLE_BUDGET_MS;
    // Every service page has a #faq: leaving /a#faq inside the settle budget had
    // the watcher find the *new* page's FAQ and scroll that page to it.
    const startedOn = pathname.current;

    const stop = () => {
      if (frame !== null) cancelAnimationFrame(frame);
      frame = null;
      READER_TOOK_OVER.forEach((type) =>
        window.removeEventListener(type, stop)
      );
    };

    const tick = () => {
      if (pathname.current !== startedOn) return stop();

      const target = document.getElementById(id);
      if (!target) return stop();

      // Where the target sits in the document. That is what moves as the page
      // settles; its offset from the viewport also moves when we scroll.
      const targetY = Math.round(
        target.getBoundingClientRect().top + window.scrollY
      );
      if (targetY !== lastTargetY) {
        lastTargetY = targetY;
        target.scrollIntoView({ behavior: "instant", block: "start" });
      }

      if (Date.now() >= deadline) return stop();
      frame = requestAnimationFrame(tick);
      return undefined;
    };

    READER_TOOK_OVER.forEach((type) =>
      window.addEventListener(type, stop, { passive: true })
    );
    frame = requestAnimationFrame(tick);

    return stop;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
